import { AccessControl, CommonForm, dispatchForm, MediaTypes, PaginationState } from '@lib/state';
import {
  getPropertyDeviceStore,
  PropertyDeviceStore,
  PropertyDeviceUIState,
} from './property-device.store';
import { GetThingsRequest, getPropertyDevice, IdentifyThingRequest } from './property-device.model';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { applyTransaction, setLoading } from '@datorama/akita';
import { PropertyDeviceForms } from './property-device.forms';

export class PropertyDeviceService {
  constructor(
    private readonly store: PropertyDeviceStore,
    private readonly thingApi: AccessControl.ThingApi
  ) {}

  getDevices(propertyId: string, request: GetThingsRequest, continuationToken?: string) {
    from(
      this.thingApi.thingGet(
        propertyId,
        continuationToken,
        undefined,
        request.thingIdentifier,
        request.thingLocationType,
        request.thingType,
        request.thingStatus
      )
    )
      .pipe(
        map(data => data.data),
        setLoading(this.store)
      )
      .subscribe(data =>
        applyTransaction(() => {
          this.store.upsertMany(data.data.map(getPropertyDevice));
          this.updateUI({ ...request });
          this.updatePaginationState({
            isDone: !data.continuationToken,
            continuationToken: data.continuationToken,
          });
        })
      );
  }

  exportDevices(propertyId: string, request: GetThingsRequest) {
    return from(
      this.thingApi.thingExportGet(
        propertyId,
        undefined,
        undefined,
        request.thingIdentifier,
        request.thingLocationType,
        request.thingType,
        request.thingStatus
      )
    )
      .pipe(
        map(x => new Blob([x.data], { type: MediaTypes.CsvFile })),
        dispatchForm(CommonForm.Export)
      )
      .subscribe(x => saveAs(x, 'PropertyDevices.csv'));
  }

  identifyDevice(propertyId: string, request: IdentifyThingRequest) {
    from(this.thingApi.thingPropertyIdIdentifyPut(propertyId, request))
      .pipe(dispatchForm(PropertyDeviceForms.IdentifyDevice))
      .subscribe();
  }

  deleteDevice(propertyId: string, thingName: string) {
    from(this.thingApi.thingPropertyIdThingNameDelete(propertyId, thingName))
      .pipe(dispatchForm(PropertyDeviceForms.DeleteDevice))
      .subscribe(() => this.store.remove(thingName));
  }

  private updateUI(state: Partial<PropertyDeviceUIState>) {
    this.store.update(({ ui }) => ({
      ui: { ...ui, ...state },
    }));
  }

  private updatePaginationState({ isDone, continuationToken }: PaginationState) {
    this.store.update(() => ({
      pagination: { isDone, continuationToken },
    }));
  }
}

// REPLACE with a lazy getter function:
let _service: PropertyDeviceService | undefined;
export function getPropertyDeviceService(): PropertyDeviceService {
  if (!_service) {
    _service = new PropertyDeviceService(getPropertyDeviceStore(), new AccessControl.ThingApi());
  }
  return _service;
}
